import React, { useState, useMemo } from 'react';
import { Container, Row, Col, Badge, Tabs, Tab, Button } from 'react-bootstrap';
import {
  Calendar,
  MapPin,
  Film,
  Headphones,
  Image as ImageIcon,
  FileText,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { jubileeStorageService } from '../services/jubileeStorageService';
import JubileeVideoPlayerModal from '../viewers/JubileeVideoPlayerModal';
import JubileeImageLightbox from '../viewers/JubileeImageLightbox';
import JubileeDocumentViewerModal from '../viewers/JubileeDocumentViewerModal';

export default function JubileeEventBrowse({ onPlayAudioTrack, onSelectEventFromParent }) {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [activeMediaTab, setActiveMediaTab] = useState('videos');

  // Modals state
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showDocModal, setShowDocModal] = useState(false);

  const events = useMemo(() => jubileeStorageService.getEvents(), []);
  const allMedia = useMemo(() => jubileeStorageService.getAllMedia(), []);

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId),
    [events, selectedEventId]
  );

  // Event media filtered and separated
  const eventMedia = useMemo(() => {
    if (!selectedEventId) return { videos: [], audios: [], images: [], documents: [] };
    const items = allMedia.filter((m) => m.eventId === selectedEventId);
    return {
      videos: items.filter((m) => m.mediaType === 'video'),
      audios: items.filter((m) => m.mediaType === 'audio'),
      images: items.filter((m) => m.mediaType === 'image'),
      documents: items.filter((m) => m.mediaType === 'document'),
    };
  }, [allMedia, selectedEventId]);

  return (
    <div className="py-4">
      <Container>
        {!selectedEventId ? (
          /* ================= EVENT DIRECTORY VIEW ================= */
          <div>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4 pb-3 border-bottom">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <div className="p-2 rounded-3 text-white d-inline-flex" style={{ background: '#198754' }}>
                    <Calendar size={22} />
                  </div>
                  <h2 className="fw-bold m-0 text-slate-900" style={{ letterSpacing: '-0.5px' }}>
                    Browse by Jubilee Events
                  </h2>
                </div>
                <p className="text-muted small m-0">
                  Explore archives organized by convocations, solemn liturgical celebrations, cultural festivals, and historical milestones.
                </p>
              </div>

              <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 rounded-pill fw-semibold">
                {events.length} Historical Events
              </span>
            </div>

            <Row className="g-4">
              {events.map((evt) => {
                const count = allMedia.filter((m) => m.eventId === evt.id).length;
                return (
                  <Col key={evt.id} xs={12} md={6} lg={4}>
                    <div
                      className="jubilee-media-card cursor-pointer h-100"
                      onClick={() => setSelectedEventId(evt.id)}
                    >
                      <div className="media-thumbnail-wrapper" style={{ height: '200px' }}>
                        <img src={evt.banner} alt={evt.title} className="media-thumbnail-img" />
                        <span className="badge bg-dark bg-opacity-75 text-warning position-absolute top-0 start-0 m-3 px-2 py-1">
                          {evt.year}
                        </span>
                        {evt.featured && (
                          <span className="badge bg-warning text-dark position-absolute top-0 end-0 m-3 px-2 py-1 fw-bold">
                            Featured
                          </span>
                        )}
                      </div>

                      <div className="media-card-body">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25" style={{ fontSize: '0.72rem' }}>
                            {evt.category}
                          </span>
                        </div>

                        <h5 className="media-card-title">{evt.title}</h5>
                        <p className="media-card-desc">{evt.description}</p>

                        {evt.location && (
                          <div className="small text-muted mb-2 d-flex align-items-center gap-1" style={{ fontSize: '0.75rem' }}>
                            <MapPin size={13} className="text-danger" />
                            <span className="text-truncate">{evt.location}</span>
                          </div>
                        )}

                        <div className="media-card-meta">
                          <span className="text-muted small">{count} Archived Items</span>
                          <span className="fw-semibold text-success small d-flex align-items-center gap-1">
                            Explore Event <ChevronRight size={14} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        ) : (
          /* ================= DEDICATED EVENT DETAIL VIEW (SEPARATE MEDIA TABS) ================= */
          <div>
            {/* Back Button & Event Header */}
            <Button
              variant="link"
              className="text-success p-0 mb-3 d-inline-flex align-items-center gap-1 text-decoration-none fw-semibold"
              onClick={() => setSelectedEventId(null)}
            >
              <ArrowLeft size={16} /> Back to All Events
            </Button>

            <div className="bg-white p-4 rounded-4 shadow-sm border mb-4">
              <Row className="g-4 align-items-center">
                <Col xs={12} md={4}>
                  <img
                    src={selectedEvent.banner}
                    alt={selectedEvent.title}
                    className="w-100 rounded-3 shadow-sm object-fit-cover"
                    style={{ height: '220px' }}
                  />
                </Col>
                <Col xs={12} md={8}>
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Badge bg="success">{selectedEvent.category}</Badge>
                    <Badge bg="secondary">Year {selectedEvent.year}</Badge>
                    {selectedEvent.date && <Badge bg="light" text="dark" className="border">{selectedEvent.date}</Badge>}
                  </div>
                  <h3 className="fw-bold text-slate-900 mb-2">{selectedEvent.title}</h3>
                  <p className="text-muted small mb-3" style={{ lineHeight: '1.6' }}>
                    {selectedEvent.description}
                  </p>
                  {selectedEvent.location && (
                    <div className="text-secondary small d-flex align-items-center gap-1">
                      <MapPin size={14} className="text-danger" />
                      <strong>Venue:</strong> {selectedEvent.location}
                    </div>
                  )}
                </Col>
              </Row>
            </div>

            {/* Separated Media Tabs for This Event */}
            <h5 className="fw-bold text-dark mb-3">Event Media Collections</h5>

            <Tabs
              activeKey={activeMediaTab}
              onSelect={(k) => setActiveMediaTab(k)}
              className="mb-4 nav-pills custom-media-tabs"
            >
              {/* VIDEO TAB */}
              <Tab
                eventKey="videos"
                title={
                  <span className="d-flex align-items-center gap-1">
                    <Film size={15} /> Videos ({eventMedia.videos.length})
                  </span>
                }
              >
                {eventMedia.videos.length > 0 ? (
                  <Row className="g-4">
                    {eventMedia.videos.map((vid) => (
                      <Col key={vid.id} xs={12} md={4}>
                        <div
                          className="jubilee-media-card cursor-pointer"
                          onClick={() => {
                            setSelectedVideo(vid);
                            setShowVideoModal(true);
                          }}
                        >
                          <div className="media-thumbnail-wrapper">
                            <img src={vid.thumbnailUrl} alt={vid.title} className="media-thumbnail-img" />
                            <span className="media-type-badge video">Video</span>
                            {vid.duration && <span className="media-duration-badge">{vid.duration}</span>}
                          </div>
                          <div className="media-card-body">
                            <h6 className="media-card-title">{vid.title}</h6>
                            <p className="media-card-desc">{vid.description}</p>
                            <span className="text-danger fw-semibold small mt-auto">Watch Video →</span>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="p-4 bg-white rounded-3 border text-center text-muted small">
                    No video recordings available for this event yet.
                  </div>
                )}
              </Tab>

              {/* AUDIO TAB */}
              <Tab
                eventKey="audios"
                title={
                  <span className="d-flex align-items-center gap-1">
                    <Headphones size={15} /> Audio ({eventMedia.audios.length})
                  </span>
                }
              >
                {eventMedia.audios.length > 0 ? (
                  <div className="d-flex flex-column gap-3">
                    {eventMedia.audios.map((aud) => (
                      <div
                        key={aud.id}
                        className="audio-card p-3"
                      >
                        <img src={aud.thumbnailUrl} alt={aud.title} className="audio-cover-art" />
                        <div className="flex-grow-1">
                          <h6 className="fw-bold m-0">{aud.title}</h6>
                          <p className="text-muted small m-0">{aud.description}</p>
                          <div className="small text-muted mt-1">Speaker: {aud.speaker || 'Archival Choir'}</div>
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          className="rounded-pill px-3"
                          onClick={() => onPlayAudioTrack && onPlayAudioTrack(aud)}
                        >
                          Listen Audio
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 bg-white rounded-3 border text-center text-muted small">
                    No audio recordings found for this event.
                  </div>
                )}
              </Tab>

              {/* IMAGES TAB */}
              <Tab
                eventKey="images"
                title={
                  <span className="d-flex align-items-center gap-1">
                    <ImageIcon size={15} /> Photographs ({eventMedia.images.length})
                  </span>
                }
              >
                {eventMedia.images.length > 0 ? (
                  <Row className="g-4">
                    {eventMedia.images.map((img, idx) => (
                      <Col key={img.id} xs={12} sm={6} md={4}>
                        <div
                          className="jubilee-media-card cursor-pointer"
                          onClick={() => {
                            setSelectedImageIndex(idx);
                            setShowImageModal(true);
                          }}
                        >
                          <div className="media-thumbnail-wrapper" style={{ height: '220px' }}>
                            <img src={img.thumbnailUrl || img.fileUrl} alt={img.title} className="media-thumbnail-img" />
                          </div>
                          <div className="media-card-body">
                            <h6 className="media-card-title">{img.title}</h6>
                            <p className="media-card-desc">{img.description}</p>
                            <span className="text-warning fw-semibold small mt-auto">Enlarge Photo →</span>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="p-4 bg-white rounded-3 border text-center text-muted small">
                    No photographs found for this event.
                  </div>
                )}
              </Tab>

              {/* DOCUMENTS TAB */}
              <Tab
                eventKey="documents"
                title={
                  <span className="d-flex align-items-center gap-1">
                    <FileText size={15} /> Documents ({eventMedia.documents.length})
                  </span>
                }
              >
                {eventMedia.documents.length > 0 ? (
                  <Row className="g-4">
                    {eventMedia.documents.map((doc) => (
                      <Col key={doc.id} xs={12} md={6}>
                        <div
                          className="bg-white p-3 rounded-3 border shadow-sm cursor-pointer d-flex align-items-center justify-content-between"
                          onClick={() => {
                            setSelectedDoc(doc);
                            setShowDocModal(true);
                          }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <div className="p-3 bg-success bg-opacity-10 text-success rounded">
                              <FileText size={24} />
                            </div>
                            <div>
                              <h6 className="fw-bold m-0">{doc.title}</h6>
                              <div className="text-muted small">{doc.pageCount ? `${doc.pageCount} Pages` : 'PDF'} • {doc.categoryName}</div>
                            </div>
                          </div>
                          <Button variant="outline-success" size="sm" className="rounded-pill">
                            Read Online
                          </Button>
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="p-4 bg-white rounded-3 border text-center text-muted small">
                    No documents uploaded for this event yet.
                  </div>
                )}
              </Tab>
            </Tabs>
          </div>
        )}
      </Container>

      {/* Modals for media preview */}
      <JubileeVideoPlayerModal
        video={selectedVideo}
        show={showVideoModal}
        onHide={() => setShowVideoModal(false)}
        allVideos={allMedia.filter((m) => m.mediaType === 'video')}
      />

      <JubileeImageLightbox
        images={eventMedia.images}
        selectedIndex={selectedImageIndex}
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        onSelectIndex={(i) => setSelectedImageIndex(i)}
      />

      <JubileeDocumentViewerModal
        document={selectedDoc}
        show={showDocModal}
        onHide={() => setShowDocModal(false)}
      />
    </div>
  );
}
